import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Flame,
  Droplet,
  Sun,
  Shield,
  Award,
  CheckCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { LondonMap } from "../components/LondonMap";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { Seo, breadcrumbSchema, faqSchema } from "../components/Seo";
import { JotformEmbed } from "../components/JotformEmbed";
import { useSiteContent } from "../lib/useSiteContent";

const HOME_FAQS = [
  {
    question: "What does end of tenancy cleaning in London include?",
    answer:
      "Our move-out cleaning in London follows the standard inventory checklist used by London letting agents — every room is deep cleaned including kitchen appliances inside and out, bathroom limescale removal, internal windows, skirting boards, light fittings, switches, sockets and door tops. We focus on the high-scrutiny areas inspectors check first.",
  },
  {
    question: "Are your tenancy cleaners insured and DBS-vetted?",
    answer:
      "Yes. Every cleaner working on a Prime Tenancy job is fully employed, trained on the AIIC inventory standard, and covered by £5M public liability insurance. We do not use day-labour or unvetted subcontractors.",
  },
  {
    question: "Do you offer a re-clean guarantee?",
    answer:
      "We do. If your landlord or letting agent flags any cleaning issue within 7 days of our visit, we return and re-clean the flagged areas at no extra cost. It is the strongest written guarantee available from any tenancy cleaner in London.",
  },
  {
    question: "How much does end of tenancy cleaning cost in London?",
    answer:
      "Studio flats start from £99, one-bedroom from £149, two-bedroom from £179 and three-bedroom homes from £230. Add-ons such as carpet steam cleaning, oven dip-tank degreasing and exterior windows are priced separately and quoted up front — no hidden fees.",
  },
  {
    question: "Can you clean on weekends or at short notice?",
    answer:
      "Yes. We operate seven days a week including Saturdays, Sundays and most public holidays, and we routinely fit in same-day and next-day jobs at the end of each month when most London tenancies end.",
  },
];

const Home = () => {
  const sc = useSiteContent("home", {
    badge: "London's Trusted End of Tenancy Cleaning",
    h1Before: "Move Out", h1Gold: "Stress-Free", h1After: "in London",
    subtitle: "Professional end of tenancy cleaning across all 32 London boroughs. Inventory-ready finishes, estate-agent standards and a 7-day re-clean guarantee.",
    services_eyebrow: "Our Services",
    services_heading: "Specialist cleaning for every London tenancy",
    services_sub: "Pick a single service or bundle them together for a bigger discount and one team that handles your entire move-out.",
    intro_eyebrow: "About Prime Tenancy",
    intro_heading: "London move-out cleaning, built around the way letting agents actually inspect.",
    intro_p1: "Prime Tenancy Cleaning is a London-based move-out cleaning company serving tenants, landlords and letting agents across all 32 boroughs. Every job we run is benchmarked against the independent inventory standard used by major London agencies, so what arrives at your property is not a generic domestic clean — it is a checkout-ready specification designed to clear an inventory check on the first visit.",
    intro_p2: "We focus exclusively on the highest-scrutiny rentals in zones 1 to 6: studios in Zone 1 mansion blocks, converted Victorian flats, modern build-to-rent towers and family homes in the outer London boroughs. Our cleaners arrive equipped with professional-grade tools, dip-tanks for oven cleaning in London, hot-water carpet extractors for end of tenancy carpet cleaning and eco-certified products that are safe around children, pets and sensitive surfaces.",
    intro_p3: "Whether you are a tenant aiming for a smooth checkout, a landlord preparing a flat for re-letting, or an agent managing a portfolio of London properties, our team gives you one dependable contractor with a written, 7-day re-clean guarantee behind every job.",
    pricing_eyebrow: "Pricing", pricing_heading: "Transparent Pricing",
    pricing_sub: "No hidden fees. Get an instant estimate based on your property size — see the full breakdown on our prices page.",
    home_faq_heading: "Frequently Asked Questions",
    home_faq_sub: "Quick answers to the questions tenants and landlords ask us most about end of tenancy cleaning in London.",
    home_faq1_q: HOME_FAQS[0].question, home_faq1_a: HOME_FAQS[0].answer,
    home_faq2_q: HOME_FAQS[1].question, home_faq2_a: HOME_FAQS[1].answer,
    home_faq3_q: HOME_FAQS[2].question, home_faq3_a: HOME_FAQS[2].answer,
    home_faq4_q: HOME_FAQS[3].question, home_faq4_a: HOME_FAQS[3].answer,
    home_faq5_q: HOME_FAQS[4].question, home_faq5_a: HOME_FAQS[4].answer,
    quote_eyebrow: "Get Started", quote_heading: "Ready for a Stress-Free Move-Out?",
    quote_sub: "Fill out our quick form below and receive a tailored end of tenancy cleaning quote within 24 hours.",
  });
  const LIVE_FAQS = [
    { question: sc.home_faq1_q, answer: sc.home_faq1_a },
    { question: sc.home_faq2_q, answer: sc.home_faq2_a },
    { question: sc.home_faq3_q, answer: sc.home_faq3_a },
    { question: sc.home_faq4_q, answer: sc.home_faq4_a },
    { question: sc.home_faq5_q, answer: sc.home_faq5_a },
  ];
  return (
    <>
      <Seo
        title="End of Tenancy Cleaning London | Prime Tenancy Cleaners"
        description="Professional end of tenancy cleaning across all 32 London boroughs. Inventory-ready, landlord approved, £5M insured, 7-day re-clean guarantee."
        ogTitle="End of Tenancy Cleaning London — 7-Day Re-Clean Guarantee"
        ogDescription="Move-out cleaning London by a fully insured, estate-agent approved team. Same-day & weekend bookings. From £99."
        path="/"
        schema={[
          breadcrumbSchema([{ name: "Home", path: "/" }]),
          faqSchema(LIVE_FAQS),
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Prime Tenancy Cleaning",
            image:
              "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80",
            telephone: "+442080363586",
            email: "info@primetenancycleaning.com",
            url: "https://primetenancycleaning.com",
            address: {
              "@type": "PostalAddress",
              addressLocality: "London",
              addressCountry: "GB",
            },
            areaServed: { "@type": "City", name: "London" },
            priceRange: "££",
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ],
                opens: "08:00",
                closes: "20:00",
              },
            ],
          },
        ]}
      />

      <HomeHero sc={sc} />
      <HomeServices sc={sc} />
      <HomeIntroCopy sc={sc} />
      <LondonMap />
      <BeforeAfterSlider />
      <HomePricing sc={sc} />
      <HomeFAQ sc={sc} faqs={LIVE_FAQS} />
      <HomeQuoteSection sc={sc} />
    </>
  );
};

// === Sections =============================================================

const HomeHero = ({ sc }) => {
  return (
  <section
    className="relative min-h-screen flex items-center justify-center overflow-hidden"
    data-testid="hero-section"
  >
    <div
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1743634421389-c4583e4575a8?w=1920&q=80')",
      }}
    />
    <div className="absolute inset-0 bg-navy/85" />

    <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-20 pt-20">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-4xl"
      >
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="inline-block bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-6"
        >
          {sc.badge}
        </motion.span>

        <h1 className="font-heading text-3xl sm:text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
          {sc.h1Before}
          <span className="text-gold"> {sc.h1Gold} </span>
          {sc.h1After}
        </h1>

        <p className="text-base sm:text-xl md:text-2xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
          {sc.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-12">
          <Link to="/get-a-quote">
            <Button
              size="lg"
              className="bg-gold hover:bg-gold-dark text-navy font-bold text-lg px-8 py-6 rounded-xl"
              data-testid="hero-cta-btn"
            >
              Get a Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <Button
            size="lg"
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-xl"
            onClick={() =>
              document
                .getElementById("coverage")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            data-testid="hero-coverage-btn"
          >
            Check Your Area
          </Button>
        </div>

        <div className="flex flex-wrap gap-6 md:gap-10">
          {[
            { icon: Shield, label: "Fully Insured", value: "£5M Cover" },
            { icon: Award, label: "Re-Clean Guarantee", value: "7-day" },
          ].map((badge, index) => (
            <motion.div
              key={badge.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="flex items-center gap-3"
            >
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <badge.icon className="w-6 h-6 text-teal" />
              </div>
              <div>
                <p className="text-white font-semibold">{badge.value}</p>
                <p className="text-slate-400 text-sm">{badge.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>

  </section>
  );
};

const HomeIntroCopy = ({ sc }) => (
  <section className="py-12 md:py-20 lg:py-28 bg-slate-50" data-testid="intro-copy">
    <div className="container mx-auto px-6 md:px-12 lg:px-20">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.intro_eyebrow}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-6 leading-tight">{sc.intro_heading}</h2>
          <div className="text-slate-700 text-lg leading-relaxed space-y-5">
            <p>{sc.intro_p1}</p>
            <p>{sc.intro_p2}</p>
            <p>{sc.intro_p3}</p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const HomeServices = ({ sc }) => {
  const services = [
    {
      icon: Sparkles,
      title: "End of Tenancy Cleaning",
      description:
        "Inventory-ready move-out cleans for tenants, landlords and London letting agents.",
      to: "/end-of-tenancy-cleaning-london",
      features: ["44-point checklist", "Re-clean guarantee", "Same-day available"],
    },
    {
      icon: Flame,
      title: "Deep Cleaning",
      description:
        "One-off deep cleans for neglected areas, post-build dust, or a periodic home reset.",
      to: "/deep-cleaning-london",
      features: [
        "Top-to-bottom treatment",
        "Limescale & grout work",
        "Trim & skirting detailing",
      ],
    },
    {
      icon: Droplet,
      title: "Carpet Cleaning",
      description:
        "Hot-water extraction that lifts deep dirt, allergens, pet odours and stubborn stains.",
      to: "/carpet-cleaning-london",
      features: ["Professional steam clean", "Fast 2–4 hr drying", "Stain treatment"],
    },
    {
      icon: Sun,
      title: "Oven Cleaning",
      description:
        "Dip-tank oven cleaning for single, double and range ovens, hobs and extractors.",
      to: "/oven-cleaning-london",
      features: ["Like-new finish", "Hob & extractor", "Eco degreasers"],
    },
  ];

  return (
    <section
      id="services"
      className="py-12 md:py-20 lg:py-28 bg-white"
      data-testid="services-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 md:mb-14"
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.services_eyebrow}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4">{sc.services_heading}</h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">{sc.services_sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className="group bg-slate-50 rounded-2xl p-7 hover:bg-navy transition-colors duration-300"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-teal/20 transition-colors">
                <service.icon className="w-7 h-7 text-teal" />
              </div>

              <h3 className="font-heading text-xl font-semibold text-navy mb-3 group-hover:text-white transition-colors">
                {service.title}
              </h3>

              <p className="text-slate-600 mb-5 group-hover:text-slate-300 transition-colors text-sm leading-relaxed">
                {service.description}
              </p>

              <ul className="space-y-2 mb-6">
                {service.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-center gap-2 text-sm text-slate-500 group-hover:text-slate-400 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4 text-teal" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link
                to={service.to}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal group-hover:text-gold transition-colors"
                data-testid={`service-link-${index}`}
              >
                Learn more
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-14 bg-navy rounded-2xl p-8 md:p-12"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gold/20 rounded-xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-gold" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-semibold text-white">
                  7-Day Re-Clean Guarantee
                </h3>
                <p className="text-slate-300">
                  If your landlord flags anything within 7 days, we return
                  free of charge.
                </p>
              </div>
            </div>
            <Link to="/get-a-quote">
              <Button
                className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8"
                data-testid="services-cta-btn"
              >
                Book With Confidence
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const HomePricing = ({ sc }) => {
  const packages = [
    {
      name: "Studio",
      price: "£99",
      description: "Perfect for studio flats",
      features: [
        "Full kitchen clean",
        "Bathroom deep clean",
        "All surfaces wiped",
        "Floor cleaning",
        "Window sills",
      ],
      popular: false,
    },
    {
      name: "1 Bedroom",
      price: "£149",
      description: "Most popular for singles & couples",
      features: [
        "Everything in Studio",
        "Bedroom deep clean",
        "Wardrobe interior",
        "Skirting boards",
        "Light fixtures",
      ],
      popular: true,
    },
    {
      name: "2 Bedroom",
      price: "£179",
      description: "Ideal for sharers",
      features: [
        "Everything in 1 Bed",
        "Second bedroom",
        "Additional bathroom",
        "Hallway & stairs",
        "Inside windows",
      ],
      popular: false,
    },
    {
      name: "3+ Bedroom",
      price: "From £230",
      description: "Family homes",
      features: [
        "Everything in 2 Bed",
        "Additional rooms",
        "Garden door cleaning",
        "Garage cleaning",
        "Custom requirements",
      ],
      popular: false,
    },
  ];

  return (
    <section
      id="pricing"
      className="py-12 md:py-20 lg:py-28 bg-slate-50"
      data-testid="pricing-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 md:mb-14"
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.pricing_eyebrow}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4">{sc.pricing_heading}</h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {sc.pricing_sub}{" "}
            <Link to="/prices" className="text-teal font-semibold">prices page</Link>.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className={`relative bg-white rounded-2xl p-4 md:p-7 ${
                pkg.popular
                  ? "ring-2 ring-gold shadow-[0_8px_30px_rgb(212,168,83,0.2)]"
                  : "shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gold text-navy px-3 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">
                  Most Popular
                </div>
              )}

              <h3 className="font-heading text-2xl font-semibold text-navy mb-2">
                {pkg.name}
              </h3>
              <p className="text-slate-500 text-sm mb-4">{pkg.description}</p>

              <div className="mb-6">
                <span className="font-heading text-4xl font-bold text-navy">
                  {pkg.price}
                </span>
                <span className="text-slate-500 text-sm ml-1">starting</span>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-center gap-2 text-slate-600"
                  >
                    <CheckCircle className="w-5 h-5 text-teal flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/get-a-quote">
                <Button
                  className={`w-full ${
                    pkg.popular
                      ? "bg-gold hover:bg-gold-dark text-navy"
                      : "bg-navy hover:bg-navy-light text-white"
                  } font-semibold`}
                >
                  Get a Quote
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center text-slate-500 mt-8"
        >
          * Prices are estimates. Final quote based on property condition and
          specific requirements.
        </motion.p>
      </div>
    </section>
  );
};

const HomeFAQ = ({ sc, faqs }) => {
  return (
    <section
      id="faq"
      className="py-12 md:py-20 lg:py-28 bg-white"
      data-testid="home-faq-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 md:mb-14"
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">FAQ</span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4">{sc.home_faq_heading}</h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {sc.home_faq_sub}{" "}For more, visit our{" "}
            <Link to="/faq" className="text-teal font-semibold">full FAQ</Link>.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion
            type="single"
            collapsible
            className="space-y-3"
            data-testid="faq-accordion"
          >
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl px-6 border-none"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-navy hover:text-teal hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

const HomeQuoteSection = ({ sc }) => {
  return (
    <section
      id="quote"
      className="py-12 md:py-20 lg:py-28 bg-navy"
      data-testid="quote-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.quote_eyebrow}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mt-3 mb-4">{sc.quote_heading}</h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">{sc.quote_sub}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="max-w-3xl mx-auto"
        >
          <div className="bg-white rounded-2xl p-4 sm:p-8 md:p-10 overflow-hidden">
            <JotformEmbed testId="home-jotform" />
            <p className="text-sm text-slate-500 mt-6 text-center">
              Prefer to talk? Call us directly:{" "}
              <a
                href="tel:+442080363586"
                className="text-teal font-semibold"
              >
                020 8036 3586
              </a>
              , or{" "}
              <Link to="/contact" className="text-teal font-semibold">
                visit our contact page
              </Link>
              .
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Home;
